/**
 * Consolidated Merchant Configuration Types
 * 
 * This file defines the centralized type definitions for merchant configuration
 * to ensure consistency across components and database operations.
 */

import { StoreFile } from './store';

/**
 * Theme configuration for branding colors
 */
export interface ThemeConfig {
  /** Primary brand color used for main UI elements */
  primary: string;
  /** Secondary brand color used for complementary UI elements */
  secondary: string;
  /** Background color for the chat interface */
  background: string;
  /** Main text color */
  text: string;
}

/**
 * Store information interface
 */
export interface StoreInfo {
  /** Store name displayed to customers */
  name: string;
  /** Store website URL */
  website?: string;
  /** Business hours formatted as text */
  hours?: string;
  /** Whether the store has multiple physical locations */
  hasMultipleLocations?: boolean;
  /** Contact information formatted as text */
  contactInfo?: string;
  /** Store timezone */
  timezone: string;
  /** Primary language used by the store */
  language: string;
  /** Currency used for pricing */
  currency: string;
  /** Measurement units used by the store */
  measurementUnit: string;
  /** Unique selling points or features of the store */
  uniqueFeatures: string;
  /** Files related to the store */
  storeFiles?: StoreFile[];
  /** Store location information */
  location?: {
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
  };
  customerServiceEmail?: string;
  customerServicePhone?: string;
  storeAddress?: string;
  aboutUs?: string;
}

/**
 * Branding configuration for the chat interface
 */
export interface BrandingConfig {
  /** Color theme configuration */
  colors: ThemeConfig;
  /** Font configuration */
  fonts: {
    primary: string;
    secondary: string;
  };
  /** URL to store logo */
  logo?: string;
  /** Title displayed in the chat window */
  chatTitle: string;
  /** Welcome message displayed before starting chat */
  welcomeMessage?: string;
  /** Icon used in the chat bubble */
  chatIcon?: string;
}

/**
 * Assistant configuration for personality and behavior
 */
export interface AssistantConfig {
  /** Name of the AI assistant */
  name: string;
  /** Description of the assistant's purpose */
  description: string;
  /** Initial greeting message sent by the assistant */
  greeting: string;
  /** Personality traits and tone of the assistant */
  persona: string;
  /** System prompt for the assistant */
  masterPrompt?: string;
  /** Source of the master prompt (auto-generated or manually edited) */
  masterPromptSource?: 'auto' | 'manual';
  /** Timestamp of when the master prompt was last edited */
  masterPromptLastEditedAt?: string;
}

/**
 * Lead capture configuration
 */
export interface LeadCaptureConfig {
  /** Required lead information fields */
  requiredFields: {
    name: string;  // 'required' | 'optional' | 'disabled'
    email: string; // 'required' | 'optional' | 'disabled'
    phone: string; // 'required' | 'optional' | 'disabled'
  };
  /** How aggressively to request lead information */
  captureStyle: string; // 'ask' | 'persistent' | 'require'
  /** Incentives offered for providing lead information */
  incentives: string;
}

/**
 * Chat technical configuration
 */
export interface ChatConfig {
  /** Temperature setting for AI responses (0.0 - 1.0) */
  temperature: number;
  /** Maximum tokens for AI responses */
  maxTokens: number;
  /** Welcome message displayed to the user */
  welcomeMessage: string;
  /** Initial greeting message sent by the assistant */
  initialGreeting: string;
  /** AI model to use (e.g., 'gpt-4', 'gpt-3.5-turbo') */
  model: string;
  /** Assistant ID for models that support it */
  assistantId?: string;
  /** Directive about the tone to use */
  toneDirective?: string;
  /** Style of conversation (professional, casual, expert) */
  conversationStyle: string;
  /** Maximum number of product recommendations to show */
  productRecommendationLimit: number;
  /** Primary goal of the chat (lead, sale, support) */
  primaryGoal: string;
  /** Creativity level for responses (0.0 - 1.0) */
  responseCreativity: number;
  /** The AI's personality traits */
  aiPersonality?: string;
  /** Name displayed for the AI assistant */
  aiName?: string;
  /** When to capture lead information */
  leadCaptureTiming?: string;
  /** Lead capture configuration */
  leadCapture: LeadCaptureConfig;
  /** Master prompt for the AI */
  masterPrompt?: string;
  /** Topics the AI should be knowledgeable about */
  educationTopics: string;
  
  // Legacy fields - can be removed once all components are updated
  initialMessage?: string;
  promptPrefix?: string;
  systemPrompt?: string;
  defaultBehavior?: string;
  isTypewriterEnabled?: boolean;
  promptGuard?: string;
  responsibleAINotice?: string;
  welcomeMessages?: string[];
  availableHours?: {
    enabled: boolean;
    timezone: string;
    schedule: Record<string, any>;
  };
}

/**
 * Feature flags for the merchant account
 */
export interface FeaturesConfig {
  /** Whether chat is enabled */
  chatEnabled: boolean;
  /** Whether product recommendations are enabled */
  recommendationsEnabled: boolean;
  /** Whether analytics are enabled */
  analyticsEnabled: boolean;
}

/**
 * Subscription details for the merchant account
 */
export interface SubscriptionConfig {
  /** Subscription plan level */
  plan: 'free' | 'pro' | 'enterprise';
  /** Subscription status */
  status: 'active' | 'trialing' | 'canceled';
  /** Enabled features based on subscription */
  features: string[];
  /** Usage limits */
  limits: {
    monthlyConversations: number;
    concurrentChats: number;
    retentionDays: number;
  };
}

/**
 * Product Category
 */
export interface ProductCategory {
  id: string;
  name: string;
  description?: string;
  parentId?: string;
  order: number;
}

/**
 * Product Attribute
 */
export interface ProductAttribute {
  id: string;
  name: string;
  type: 'text' | 'number' | 'boolean' | 'select';
  required: boolean;
  options?: string[];
  isKeyAttribute: boolean;
  matchWeight: number;
}

/**
 * Product Configuration
 */
export interface ProductConfig {
  total?: number;
  active?: number;
  lastUpdated?: string;
  categories?: ProductCategory[];
  attributes?: ProductAttribute[];
  matchingAlgorithm?: 'attribute' | 'semantic' | 'hybrid';
  matchThreshold?: number;
  recommendationOrder?: 'score' | 'price_asc' | 'price_desc' | 'popularity';
  keyAttributeWeight?: 'balanced' | 'high' | 'low';
  dataSource?: 'firestore' | 'shopify' | 'woocommerce' | 'api';
  inventoryChecking?: 'disabled' | 'soft' | 'strict';
  cacheTTL?: number;
  customAPIEndpoint?: string;
}

/**
 * Beta feature flags for testing new functionality
 * These are specific feature flags for features in development
 */
export interface BetaFlagsConfig {
  [key: string]: boolean;
}

/**
 * Complete merchant configuration
 * This is the single source of truth for all merchant settings
 */
export interface MerchantConfig {
  /** Store information */
  storeInfo: StoreInfo;
  /** Branding configuration */
  branding: BrandingConfig;
  /** Chat technical configuration */
  chatConfig: ChatConfig;
  /** Assistant personality configuration */
  assistant: AssistantConfig;
  /** Feature flags */
  features: FeaturesConfig;
  /** Subscription details */
  subscription: SubscriptionConfig;
  /** Products configuration */
  products?: ProductConfig;
  
  /**
   * Beta feature flags for new functionality
   */
  betaFlags?: BetaFlagsConfig;
}

/**
 * Default configuration values
 * Used when no configuration exists yet
 */
export const DEFAULT_MERCHANT_CONFIG: MerchantConfig = {
  storeInfo: {
    name: 'MattressAI Store',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: 'en',
    currency: 'USD',
    measurementUnit: 'imperial',
    hasMultipleLocations: false,
    uniqueFeatures: '',
    storeFiles: []
  },
  branding: {
    colors: {
      primary: '#2563eb',
      secondary: '#64748b',
      background: '#ffffff',
      text: '#000000'
    },
    fonts: {
      primary: 'Inter',
      secondary: 'system-ui'
    },
    chatTitle: 'MattressAI Chat',
    welcomeMessage: 'Welcome! How can I help you find the perfect mattress?',
    chatIcon: 'default'
  },
  chatConfig: {
    temperature: 0.7,
    maxTokens: 2000,
    welcomeMessage: 'Welcome! How can I help you find the perfect mattress?',
    initialGreeting: 'Welcome! How can I help you find the perfect mattress?',
    model: 'gpt-4',
    conversationStyle: 'professional',
    productRecommendationLimit: 3,
    primaryGoal: 'lead',
    responseCreativity: 0.7,
    leadCaptureTiming: 'before-recommendations',
    leadCapture: {
      requiredFields: {
        name: 'disabled',
        email: 'optional',
        phone: 'optional'
      },
      captureStyle: 'ask',
      incentives: 'Info about sales and deals'
    },
    masterPrompt: 'You are a helpful AI assistant for mattress recommendations.',
    educationTopics: 'sleep health, mattress types, sleep positions, mattress materials',
    aiName: 'MattressAI Assistant',
    
    // Legacy fields - keeping for backward compatibility
    initialMessage: 'Welcome! How can I help you find the perfect mattress?',
    promptPrefix: 'You are a helpful assistant',
    systemPrompt: 'You are a helpful assistant',
    defaultBehavior: 'friendly',
    isTypewriterEnabled: true,
    promptGuard: 'Be friendly and professional',
    responsibleAINotice: 'Responsible AI Notice',
    welcomeMessages: ['Welcome! How can I help you find the perfect mattress?'],
    availableHours: {
      enabled: true,
      timezone: 'UTC',
      schedule: {}
    }
  },
  assistant: {
    name: 'MattressAI Assistant',
    description: 'A helpful mattress expert that provides personalized recommendations',
    greeting: 'Hello! I\'m your mattress expert. How can I help you today?',
    persona: 'A knowledgeable and friendly sleep expert'
  },
  features: {
    chatEnabled: true,
    recommendationsEnabled: true,
    analyticsEnabled: true
  },
  subscription: {
    plan: 'free',
    status: 'trialing',
    features: ['chat', 'analytics'],
    limits: {
      monthlyConversations: 100,
      concurrentChats: 1,
      retentionDays: 30
    }
  },
  products: {
    total: 0,
    active: 0,
    categories: [],
    attributes: [],
    matchingAlgorithm: 'attribute',
    matchThreshold: 0.7,
    recommendationOrder: 'score',
    keyAttributeWeight: 'balanced',
    dataSource: 'firestore',
    inventoryChecking: 'disabled',
    cacheTTL: 60
  },
  betaFlags: {}
}; 